import { getDashboardDigest } from "@hp/api-client";
import { CLAIM_CANDIDATES } from "../mocks/fixtures";
import { formatForum, formatINRRange, formatIssueTag } from "../lib/format";
import { USE_MOCK_DATA } from "./config";
import { simulateNetwork } from "./simulateNetwork";
import type { DigestPeriod } from "./types";

interface PeriodDef {
  key: string;
  label: string;
  start: string;
  endExclusive: string;
  isCurrent: boolean;
  generatedAt: string;
}

// "Today" for this fixture universe is 2026-09-11, so the current digest
// period is a partial month. Newest first, matching how a period switcher
// reads: current period, then history behind it.
const PERIOD_DEFS: PeriodDef[] = [
  {
    key: "2026-09",
    label: "September 2026 (through Sep 11)",
    start: "2026-09-01T00:00:00Z",
    endExclusive: "2026-09-12T00:00:00Z",
    isCurrent: true,
    generatedAt: "2026-09-11T07:00:00Z",
  },
  {
    key: "2026-08",
    label: "August 2026",
    start: "2026-08-01T00:00:00Z",
    endExclusive: "2026-09-01T00:00:00Z",
    isCurrent: false,
    generatedAt: "2026-09-01T07:00:00Z",
  },
  {
    key: "2026-07",
    label: "July 2026",
    start: "2026-07-01T00:00:00Z",
    endExclusive: "2026-08-01T00:00:00Z",
    isCurrent: false,
    generatedAt: "2026-08-01T07:00:00Z",
  },
];

function buildPeriod(def: PeriodDef): DigestPeriod {
  const items = CLAIM_CANDIDATES.filter((c) => c.created_at >= def.start && c.created_at < def.endExclusive).sort(
    (a, b) => b.value_high - a.value_high,
  );

  const combinedValueLow = items.reduce((sum, c) => sum + c.value_low, 0);
  const combinedValueHigh = items.reduce((sum, c) => sum + c.value_high, 0);

  const top = items[0];
  const highlight = top
    ? `Largest new candidate: ${formatIssueTag(top.issue_tag)} before the ${formatForum(top.recommended_forum)}, valued ${formatINRRange(top.value_low, top.value_high)}.`
    : "No new claim candidates were identified this period.";

  return {
    period_key: def.key,
    label: def.label,
    is_current: def.isCurrent,
    period_start: def.start,
    period_end_exclusive: def.endExclusive,
    generated_at: def.generatedAt,
    new_claim_candidates: items,
    new_count: items.length,
    combined_value_low: combinedValueLow,
    combined_value_high: combinedValueHigh,
    highlight,
  };
}

/**
 * The periodic digest history, newest period first. A real backend would
 * likely serve this from a scheduled job's output rather than deriving it
 * live from the candidate list, but the shape returned here - an array of
 * already-summarized periods - is what this app's digest view is built
 * against either way.
 */
export function fetchDigestHistory(): Promise<DigestPeriod[]> {
  if (!USE_MOCK_DATA) return getDashboardDigest(PERIOD_DEFS.length);
  return simulateNetwork(PERIOD_DEFS.map(buildPeriod));
}
